import {
  shouldModelWithoutFieldBeIncluded,
  getRelativeDateValue,
  isDateRangeInRelativeDateRange,
  type AttributeComparator,
} from '@ardoq/filter-helpers';
import type {
  FormattingRuleOperator,
  QueryBuilderRuleValue,
} from '@ardoq/api-types';

type AreAttributesIncludedArgs = {
  filterAttributeNames: string[];
  isNegative: boolean;
  filterValue: QueryBuilderRuleValue;
  isFormattingFilter: boolean;
  filterType: string | undefined;
  filterComparator: FormattingRuleOperator | undefined;
  comparator: AttributeComparator | undefined;
  attributes: Record<string, unknown>;
};

/**
 * Pure function to check if an entity's attributes match a filter with multiple attribute names.
 *
 * This function handles filters that check multiple attributes (comma-separated names),
 * such as date range filters that check both start and end dates.
 *
 * @param filterAttributeNames - Array of attribute names to check (e.g., ['startDate', 'endDate'])
 * @param isNegative - Whether to negate the comparison result
 * @param filterValue - The value to compare against
 * @param isFormattingFilter - Whether this is a formatting filter
 * @param filterType - The type of filter (e.g., 'DATE_RANGE')
 * @param filterComparator - The comparison operator
 * @param comparator - The comparator function to use
 * @param attributes - The attributes object from the entity
 */
export const areAttributesIncluded = ({
  filterAttributeNames,
  isNegative,
  filterValue,
  isFormattingFilter,
  filterType,
  filterComparator,
  comparator,
  attributes,
}: AreAttributesIncludedArgs): boolean => {
  const hasAttributes = filterAttributeNames.every(
    filterAttributeName => filterAttributeName in attributes
  );

  if (hasAttributes) {
    // Check for relative date formatting (for DATE_RANGE fields)
    if (filterComparator) {
      const relativeDateValue = getRelativeDateValue(
        isFormattingFilter,
        filterValue,
        filterType,
        filterComparator
      );
      if (relativeDateValue) {
        // For date range fields, filterAttributeNames is [startField, endField]
        const [startVal, endVal] = filterAttributeNames.map(
          name => attributes[name]
        );
        return isDateRangeInRelativeDateRange(
          startVal,
          endVal,
          relativeDateValue
        );
      }
    }

    const values = filterAttributeNames.map(
      filterAttributeName => attributes[filterAttributeName]
    );
    const isIncluded = comparator?.isIncluded(values, filterValue) ?? false;
    return isNegative ? !isIncluded : isIncluded;
  }

  return shouldModelWithoutFieldBeIncluded({
    isNegative,
    value: filterValue,
    comparator: filterComparator,
  });
};
